import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { groupCRMUsersByDTC } from '../../utils/crmUserSorting';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  IconButton,
  Tooltip,
  useDisclosure,
  Text,
  useColorModeValue,
  Badge,
  Select,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTasks } from '../../hooks/useTasks';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import TaskDetailModal from '../../components/Tasks/TaskDetailModal';
import TaskCompletionActivityPrompt, { taskHasLoggableEntity } from '../../components/CRM/TaskCompletionActivityPrompt';
import EventDetailDrawer from '../../components/CRM/Calendar/EventDetailDrawer';
import UnifiedEventModal from '../../components/CRM/Calendar/UnifiedEventModal';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../utils/init-firebase';
import './calendar-styles.css';

const localizer = momentLocalizer(moment);

export default function CalendarPage() {
  const history = useHistory();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filteredUserId, setFilteredUserId] = useState('');
  const [crmUsers, setCrmUsers] = useState([]);
  const [completedTaskForPrompt, setCompletedTaskForPrompt] = useState(null);
  const [showTasks, setShowTasks] = useState(true);
  const [showEvents, setShowEvents] = useState(true);
  const [showMeetings, setShowMeetings] = useState(true);
  const [createEventInitialDate, setCreateEventInitialDate] = useState(null);
  
  const { isOpen: isTaskDetailOpen, onOpen: onTaskDetailOpen, onClose: onTaskDetailClose } = useDisclosure();
  const { isOpen: isActivityPromptOpen, onOpen: onActivityPromptOpen, onClose: onActivityPromptClose } = useDisclosure();
  const { isOpen: isEventDetailOpen, onOpen: onEventDetailOpen, onClose: onEventDetailClose } = useDisclosure();
  const { isOpen: isUnifiedEventOpen, onOpen: onUnifiedEventOpen, onClose: onUnifiedEventClose } = useDisclosure();

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const effectiveUserId = filteredUserId || currentUser?.uid;

  // Get visible calendar range for optimized queries
  const getVisibleRange = () => {
    const start = moment(currentDate).startOf(view).subtract(7, 'days').toDate();
    const end = moment(currentDate).endOf(view).add(7, 'days').toDate();
    return { start, end };
  };

  const visibleRange = getVisibleRange();

  const { tasks, createTask, updateTask, deleteTask } = 
    useTasks(effectiveUserId);

  const { events, updateEvent, deleteEvent } = 
    useCalendarEvents(effectiveUserId, visibleRange.start, visibleRange.end);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headerBG = useColorModeValue('white', 'gray.800');
  const calendarBG = useColorModeValue('white', 'gray.800');
  const filterBG = useColorModeValue('gray.50', 'gray.700');

  // Load CRM users for admin filter
  useEffect(() => {
    if (!isCRMAdmin) return;

    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('CRMUser', '==', true));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const users = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          uid: doc.id,
          name: userData.Name || userData.name || userData.email,
          ...userData,
        });
      });
      setCrmUsers(users);
    });

    return () => unsubscribe();
  }, [isCRMAdmin]);

  // Convert tasks and events to calendar items
  const calendarItems = useMemo(() => {
    const items = [];

    // Add tasks if enabled
    if (showTasks) {
      tasks.forEach(task => {
        const dueDate = task.dueDate?.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
        
        let start, end;
        if (task.dueTime) {
          const [hours, minutes] = task.dueTime.split(':');
          start = new Date(dueDate);
          start.setHours(parseInt(hours), parseInt(minutes), 0);
          end = new Date(start.getTime() + (task.eventDuration || 30) * 60000);
        } else {
          start = new Date(dueDate);
          start.setHours(0, 0, 0);
          end = new Date(dueDate);
          end.setHours(23, 59, 59);
        }

        items.push({
          id: `task-${task.id}`,
          title: task.title,
          start,
          end,
          allDay: !task.dueTime,
          resource: task,
          type: 'task',
        });
      });
    }

    // Add events if enabled (filter by meeting type if applicable)
    if (showEvents || showMeetings) {
      events.forEach(event => {
        const start = event.startTime?.toDate ? event.startTime.toDate() : new Date(event.startTime);
        const end = event.endTime?.toDate ? event.endTime.toDate() : new Date(event.endTime);

        const isMeeting = event.eventType === 'meeting';
        const shouldShow = (isMeeting && showMeetings) || (!isMeeting && showEvents);

        if (shouldShow) {
          items.push({
            id: `event-${event.id}`,
            title: event.title,
            start,
            end,
            allDay: event.allDay,
            resource: event,
            type: 'event',
            subType: event.eventType || 'general',
          });
        }
      });
    }

    return items;
  }, [tasks, events, showTasks, showEvents, showMeetings]);

  const handleSelectSlot = useCallback((slotInfo) => {
    // Open unified modal with selected date
    setCreateEventInitialDate(slotInfo.start);
    onUnifiedEventOpen();
  }, [onUnifiedEventOpen]);

  const handleSelectEvent = useCallback((calendarItem) => {
    // Open appropriate detail view based on type
    if (calendarItem.type === 'task') {
      setSelectedTask(calendarItem.resource);
      onTaskDetailOpen();
    } else if (calendarItem.type === 'event') {
      setSelectedEvent(calendarItem.resource);
      onEventDetailOpen();
    }
  }, [onTaskDetailOpen, onEventDetailOpen]);

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const handleDeleteTask = async (taskId) => {
    return await deleteTask(taskId);
  };

  // Completing a task linked to a contact/deal offers to log the interaction
  // as a CRM activity. The task is captured before completing because hook
  // refetches can reshuffle the list.
  const handleCompleteTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (task && task.status === 'completed') {
      // Uncompleting task
      await updateTask(taskId, { status: 'pending', completedAt: null });
    } else {
      // Completing task
      const result = await updateTask(taskId, { status: 'completed', completedAt: new Date() });
      if (result?.success && taskHasLoggableEntity(task)) {
        setCompletedTaskForPrompt(task);
        onActivityPromptOpen();
      }
    }
  };

  const handleActivityPromptClose = () => {
    onActivityPromptClose();
    setCompletedTaskForPrompt(null);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setSelectedTask(null);
    onTaskDetailClose();
    onUnifiedEventOpen();
  };

  const handleUnifiedEventClose = () => {
    setEditingTask(null);
    onUnifiedEventClose();
  };

  const eventStyleGetter = (calendarItem) => {
    let backgroundColor = '#3182ce'; // Default blue
    let borderLeft = 'none';
    
    if (calendarItem.type === 'task') {
      const task = calendarItem.resource;
      // Color by task status
      if (task.status === 'completed') {
        backgroundColor = '#48bb78'; // Green for completed
      } else if (task.isCalendarEvent) {
        backgroundColor = '#805ad5'; // Purple for meetings/events
      } else if (task.dueTime) {
        backgroundColor = '#3182ce'; // Blue for timed tasks
      } else {
        backgroundColor = '#63b3ed'; // Light blue for all-day tasks
      }

      return {
        style: {
          backgroundColor,
          borderRadius: '4px',
          opacity: task.status === 'completed' ? 0.6 : 1,
          color: 'white',
          border: 'none',
          display: 'block',
        }
      };
    } else if (calendarItem.type === 'event') {
      const event = calendarItem.resource;
      // Calendar events have distinct styling
      backgroundColor = '#9F7AEA'; // Purple for events
      
      // Add video meeting indicator
      if (event.googleMeetLink || event.zoomMeetingLink) {
        borderLeft = '4px solid #38B2AC'; // Teal border for video meetings
      }
      
      // Google sync state indicator (from sync_status)
      if (event.syncStatus === 'error') {
        borderLeft = '4px solid #E53E3E'; // Red border for sync errors
      } else if (event.syncStatus === 'pending') {
        borderLeft = '4px solid #ED8936'; // Orange border while syncing
      } else if (event.syncedWithGoogle) {
        borderLeft = '4px solid #48BB78'; // Green border for synced
      }

      return {
        style: {
          backgroundColor,
          borderRadius: '4px',
          color: 'white',
          border: 'none',
          borderLeft,
          display: 'block',
          fontWeight: '500',
        }
      };
    }

    return { style: { backgroundColor } };
  };

  const todayTasks = tasks.filter(t => {
    if (!t.dueDate) return false;
    const dueDate = t.dueDate?.toDate ? t.dueDate.toDate() : t.dueDate;
    return moment(dueDate).isSame(moment(), 'day');
  });

  const upcomingTasks = tasks.filter(t => {
    if (!t.dueDate || t.status === 'completed') return false;
    const dueDate = moment(t.dueDate?.toDate ? t.dueDate.toDate() : t.dueDate);
    return dueDate.isAfter(moment(), 'day') && dueDate.isBefore(moment().add(7, 'days'));
  });

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="100%" px={{ base: 4, md: 6, lg: 8 }} py={6}>
        <VStack align="stretch" spacing={6} maxW="1600px" mx="auto">
          {/* Header */}
          <Box bg={headerBG} p={6} borderRadius="xl" shadow="sm">
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3}>
                <Tooltip label="Back to My Deals">
                  <IconButton
                    icon={<FiArrowLeft />}
                    aria-label="Back to My Deals"
                    onClick={() => history.push('/crm/my-deals')}
                    size="lg"
                    variant="ghost"
                  />
                </Tooltip>
                <VStack align="flex-start" spacing={1}>
                  <Heading size="lg">
                    {filteredUserId 
                      ? `📅 ${crmUsers.find(u => u.uid === filteredUserId)?.name || 'User'}'s Calendar`
                      : '📅 Calendar'}
                  </Heading>
                  <Text color="gray.600" fontSize="sm">
                    {filteredUserId ? 'Viewing another user\'s schedule' : 'Manage your schedule and tasks'}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={2}>
                <Badge colorScheme="blue">{todayTasks.length} Today</Badge>
                <Badge colorScheme="purple">{upcomingTasks.length} Upcoming</Badge>
                
                {/* Filter Toggles */}
                <HStack spacing={2} ml={2}>
                  <Button
                    size="sm"
                    variant={showTasks ? 'solid' : 'outline'}
                    colorScheme="cyan"
                    onClick={() => setShowTasks(!showTasks)}
                  >
                    Tasks
                  </Button>
                  <Button
                    size="sm"
                    variant={showMeetings ? 'solid' : 'outline'}
                    colorScheme="purple"
                    onClick={() => setShowMeetings(!showMeetings)}
                  >
                    Meetings
                  </Button>
                  <Button
                    size="sm"
                    variant={showEvents ? 'solid' : 'outline'}
                    colorScheme="blue"
                    onClick={() => setShowEvents(!showEvents)}
                  >
                    Events
                  </Button>
                </HStack>

                <Button
                  leftIcon={<FiPlus />}
                  colorScheme="blue"
                  onClick={() => {
                    setCreateEventInitialDate(new Date());
                    onUnifiedEventOpen();
                  }}
                  shadow="sm"
                  size="sm"
                >
                  Add Event
                </Button>
              </HStack>
            </HStack>

            {/* Admin User Filter & Color Legend */}
            <HStack mt={4} spacing={4} flexWrap="wrap">
              {isCRMAdmin && (
                <Box p={4} bg={filterBG} borderRadius="md" flex={1}>
                  <FormControl maxW="300px">
                    <FormLabel fontSize="xs" mb={1} textTransform="uppercase" fontWeight="semibold" color="gray.600">
                      View Calendar For
                    </FormLabel>
                    <Select
                      size="sm"
                      placeholder="My Calendar"
                      value={filteredUserId}
                      onChange={(e) => setFilteredUserId(e.target.value)}
                    >
                      {(() => {
                        const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                        return (
                          <>
                            {dtcUsers.length > 0 && (
                              <optgroup label="DTC Loan Officers">
                                {dtcUsers.map((user) => (
                                  <option key={user.uid} value={user.uid}>{user.name}</option>
                                ))}
                              </optgroup>
                            )}
                            <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                              {otherUsers.map((user) => (
                                <option key={user.uid} value={user.uid}>{user.name}</option>
                              ))}
                            </optgroup>
                          </>
                        );
                      })()}
                    </Select>
                  </FormControl>
                </Box>
              )}
              
              {/* Color Legend */}
              <Box p={4} bg={filterBG} borderRadius="md">
                <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2} textTransform="uppercase">
                  Legend
                </Text>
                <HStack spacing={3} flexWrap="wrap">
                  {showMeetings && (
                    <>
                      <HStack spacing={1}>
                        <Box w="12px" h="12px" bg="#9F7AEA" borderRadius="sm" />
                        <Text fontSize="xs">Meeting</Text>
                      </HStack>
                      <HStack spacing={1}>
                        <Box w="12px" h="12px" bg="#9F7AEA" borderRadius="sm" borderLeft="3px solid #38B2AC" />
                        <Text fontSize="xs">Video Meeting</Text>
                      </HStack>
                    </>
                  )}
                  {showEvents && (
                    <HStack spacing={1}>
                      <Box w="12px" h="12px" bg="#6B46C1" borderRadius="sm" />
                      <Text fontSize="xs">Event</Text>
                    </HStack>
                  )}
                  {showTasks && (
                    <>
                      <HStack spacing={1}>
                        <Box w="12px" h="12px" bg="#3182ce" borderRadius="sm" />
                        <Text fontSize="xs">Task</Text>
                      </HStack>
                      <HStack spacing={1}>
                        <Box w="12px" h="12px" bg="#48bb78" borderRadius="sm" opacity={0.6} />
                        <Text fontSize="xs">Completed</Text>
                      </HStack>
                    </>
                  )}
                </HStack>
              </Box>
            </HStack>
          </Box>

          {/* Calendar */}
          <Box bg={calendarBG} p={6} borderRadius="xl" shadow="sm" minH="600px">
            <Calendar
              localizer={localizer}
              events={calendarItems}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              view={view}
              date={currentDate}
              selectable
              popup
              eventPropGetter={eventStyleGetter}
              views={['month', 'week', 'day']}
            />
          </Box>
        </VStack>

        {/* Unified Event/Task Creation + Edit Modal */}
        <UnifiedEventModal
          isOpen={isUnifiedEventOpen}
          onClose={handleUnifiedEventClose}
          onSave={async () => {
            // Refresh will happen automatically via hooks
          }}
          initialDate={createEventInitialDate}
          editingTask={editingTask}
          crmUsers={crmUsers}
        />

        {/* Task Detail Modal */}
        <TaskDetailModal
          isOpen={isTaskDetailOpen}
          onClose={onTaskDetailClose}
          task={selectedTask}
          onEdit={handleEditTask}
          onDelete={handleDeleteTask}
          onComplete={handleCompleteTask}
          onReopen={handleCompleteTask}
          assignedUserName={selectedTask?.assignedTo ? crmUsers.find(u => u.uid === selectedTask.assignedTo)?.name || 'Unknown' : null}
          createdByUserName={selectedTask?.createdBy ? crmUsers.find(u => u.uid === selectedTask.createdBy)?.name || 'Unknown' : null}
        />

        {/* Post-completion "log an activity?" prompt */}
        <TaskCompletionActivityPrompt
          isOpen={isActivityPromptOpen}
          onClose={handleActivityPromptClose}
          task={completedTaskForPrompt}
          onCreateTask={(taskData) => createTask(taskData)}
        />

        {/* Event Detail Drawer */}
        <EventDetailDrawer
          isOpen={isEventDetailOpen}
          onClose={onEventDetailClose}
          event={selectedEvent}
          onUpdate={updateEvent}
          onDelete={deleteEvent}
        />
      </Container>
    </Box>
  );
}

